import { ENVIRONMENT_INITIALIZER, Provider, inject } from '@angular/core';
import { ControlFactory } from './control-factory';
import { ComponentRegistry } from './component-registry';
import { TextReadonlyComponent } from './text-readonly/text-readonly.component';
import { TextareaComponent } from './textarea/textarea.component';
import { RemarksComponent } from './remarks/remarks.component';
import { DateTimeComponent } from './date-time/date-time.component';
import { TextInputComponent } from './text-input/text-input.component';
import { NumberInputComponent } from './number-input/number-input.component';
import { CheckboxGroupComponent } from './checkbox-group/checkbox-group.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { RadioInputComponent } from './radio-input/radio-input.component';
import { FileRef } from './file-upload/file-upload.types';
import { ImageUploadComponent } from './image-upload/image-upload.component';
import { UnsupportedFieldComponent } from './unsupported-field/unsupported-field.component';
import { DividerComponent } from './divider/divider.component';
import { DateTimeInputComponent } from './date-time-input/date-time-input.component';
import { VspRegulationLookupValue } from './vsp-regulation-lookup/vsp-regulation-lookup.component';
import { VspRegulationLookupListComponent } from './vsp-regulation-lookup/vsp-regulation-lookup-list.component';
import { ShipsanRegulationLookupValue } from './shipsan-regulation-lookup/shipsan-regulation-lookup.component';
import { ShipsanRegulationLookupListComponent } from './shipsan-regulation-lookup/shipsan-regulation-lookup-list.component';
import { parseJsonObjectValue } from './json-object-value';

const VSP_REGULATION_LOOKUP_TYPE = 'vsp-regulation-lookup';
const SHIPSAN_REGULATION_LOOKUP_TYPE = 'shipsan-regulation-lookup';

export function provideFieldControls(): Provider {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue: () => {
      const cf = inject(ControlFactory);

      for (const type of ['text', 'text-input', 'textarea', 'remarks']) {
        cf.register(type, value => cf.coerce(type, value));
      }
      cf.register('number-input', value =>
        typeof value === 'number' ? value : null
      );
      cf.register(
        'number',
        value => (value as number | null | undefined) ?? null
      );
      cf.register(
        'checkbox',
        value => (value as boolean | null | undefined) ?? false
      );
      for (const type of ['text-readonly', 'date', 'time', 'datetime']) {
        cf.register(
          type,
          value => (value as string | null | undefined) ?? null
        );
      }
      cf.register(
        'checkbox-group',
        value => (Array.isArray(value) ? (value as string[]) : []),
        'list'
      );
      for (const type of ['dropdown', 'radio-input']) {
        cf.register(type, value => (typeof value === 'string' ? value : null));
      }
      cf.register(
        'dropdown-multiple',
        value => (Array.isArray(value) ? (value as string[]) : []),
        'list'
      );
      cf.register(
        'image-upload',
        value => (Array.isArray(value) ? (value as FileRef[]) : []),
        'list'
      );
      cf.register(
        VSP_REGULATION_LOOKUP_TYPE,
        parseJsonObjectValue<
          VspRegulationLookupValue | VspRegulationLookupValue[]
        >
      );
      cf.register(
        SHIPSAN_REGULATION_LOOKUP_TYPE,
        parseJsonObjectValue<
          ShipsanRegulationLookupValue | ShipsanRegulationLookupValue[]
        >
      );
      cf.register('date-time-input', value =>
        typeof value === 'string' ? value : null
      );
      cf.register('divider', () => null);
      cf.register('unsupported-field', () => null);
    },
  };
}

export function provideFieldComponents(): Provider {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue: () => {
      const cr = inject(ComponentRegistry);

      cr.register('text-readonly', TextReadonlyComponent);
      cr.register('textarea', TextareaComponent);
      cr.register('remarks', RemarksComponent);
      cr.register('text', TextInputComponent);
      cr.register('text-input', TextInputComponent);
      cr.register('number-input', NumberInputComponent);
      cr.register('checkbox-group', CheckboxGroupComponent);
      cr.register('dropdown', DropdownComponent);
      cr.register('dropdown-multiple', DropdownComponent);
      cr.register('radio-input', RadioInputComponent);
      cr.register('date', DateTimeComponent);
      cr.register('time', DateTimeComponent);
      cr.register('datetime', DateTimeComponent);
      cr.register('image-upload', ImageUploadComponent);
      cr.register(VSP_REGULATION_LOOKUP_TYPE, VspRegulationLookupListComponent);
      cr.register(
        SHIPSAN_REGULATION_LOOKUP_TYPE,
        ShipsanRegulationLookupListComponent
      );
      cr.register('date-time-input', DateTimeInputComponent);
      cr.register('divider', DividerComponent);
      cr.register('unsupported-field', UnsupportedFieldComponent);
    },
  };
}
